import { GameOptions } from '../managers/NetworkManager';

interface AvatarOption {
  id: number;
  name: string;
  previewSrc: string;
  color: string;
  previewPath: string;
}

export class InitialDialog {
  private container: HTMLDivElement;
  private avatarOptions: AvatarOption[];
  private currentAvatarIndex: number = 0;
  private startGameCallback: (options: GameOptions) => void;
  private gameMode: 'open' | 'private' = 'open';
  private gameCode: string = '';
  private isHomeTeam: boolean = true;
  private playerName: string = '';
  private loadingProgressBar: HTMLDivElement | null = null;
  private loadingProgressText: HTMLDivElement | null = null;
  
  constructor(startGameCallback: (options: GameOptions) => void) {
    this.startGameCallback = startGameCallback;
    
    // Load player name from local storage if available
    const savedName = localStorage.getItem('playerName');
    if (savedName) {
      this.playerName = savedName;
    }

    // Load avatar selection from local storage if available
    const savedAvatarIndex = localStorage.getItem('selectedAvatarIndex');
    if (savedAvatarIndex !== null) {
      this.currentAvatarIndex = parseInt(savedAvatarIndex, 10);
    }
    
    // Create avatar options with different colors
    this.avatarOptions = [
      { id: 0, name: 'michael', previewSrc: '', color: '#FF5252', previewPath: '/assets/models/mj/preview.png' }, // Red
      { id: 1, name: 'larry', previewSrc: '', color: '#448AFF', previewPath: '/assets/models/lb/preview.png' }, // Blue
      { id: 2, name: 'lebron', previewSrc: '', color: '#66BB6A', previewPath: '/assets/models/lj/preview.png' }, // Green
      { id: 3, name: 'steph', previewSrc: '', color: '#FFC107', previewPath: '/assets/models/sc/preview.png' }, // Amber
      { id: 4, name: 'pieter', previewSrc: '', color: '#AB47BC', previewPath: '/assets/models/pl/preview.png' }, // Purple
      { id: 5, name: 'andrej', previewSrc: '', color: '#FF7043', previewPath: '/assets/models/ak/preview.png' }, // Deep Orange
      /*
      { id: 6, name: 'Riley', previewSrc: '', color: '#26C6DA' }, // Cyan
      { id: 7, name: 'Jasmine', previewSrc: '', color: '#EC407A' }, // Pink
      { id: 8, name: 'Taylor', previewSrc: '', color: '#9CCC65' }  // Light Green
       */
    ];
    
    // Create container for the dialog
    this.container = document.createElement('div');
    this.container.classList.add('modal-overlay');
    
    // Add landscape-specific styles
    const landscapeStyles = document.createElement('style');
    landscapeStyles.textContent = `
      @media (max-height: 600px) and (orientation: landscape) {
        .modal-content {
          width: 95%;
          max-width: 800px;
          padding: 1rem;
          max-height: 90vh;
          overflow-y: auto;
        }
        
        .game-logo {
          font-size: 2rem;
          margin-bottom: 1rem;
        }
        
        .avatar-preview {
          width: 80px;
          height: 80px;
        }
        
        .form-group {
          margin-bottom: 0.75rem;
        }
        
        .form-group label {
          font-size: 0.9rem;
        }
        
        .radio-group {
          gap: 0.5rem;
        }
        
        button {
          padding: 0.5rem 1rem;
          font-size: 0.9rem;
        }
        
        input[type="text"] {
          padding: 0.5rem;
          font-size: 0.9rem;
        }
        
        .carousel button {
          width: 40px;
          height: 40px;
          font-size: 1.5rem;
          margin: 0 10px;
        }
        
        .avatar-container {
          min-width: 120px;
        }
      }

      @media (max-width: 768px) {
        .modal-overlay {
          align-items: flex-start;
          padding-top: 1rem;
        }

        .modal-content {
          margin-top: 0;
          max-height: 95vh;
          overflow-y: auto;
        }
      }
    `;
    document.head.appendChild(landscapeStyles);
    
    // Check if there's a game code or roomId in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const codeParam = urlParams.get('code');
    const roomIdParam = urlParams.get('roomId');
    if (codeParam || roomIdParam) {
      this.gameMode = 'private';
      this.gameCode = codeParam || roomIdParam || '';
    }
    
    // Build the dialog content
    this.buildDialogContent();
    
    // Add the container to the UI container
    const uiContainer = document.getElementById('ui-container');
    if (uiContainer) {
      uiContainer.appendChild(this.container);
    }
  }
  
  public show(): void {
    this.container.style.display = 'flex';
  }
  
  public hide(): void {
    this.container.style.display = 'none';
  }
  
  public showError(message: string): void {
    const errorElement = document.getElementById('error-message');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
    }
    this.show();
  }
  
  private buildDialogContent(): void {
    const content = document.createElement('div');
    content.classList.add('modal-content');
    
    // Create left and right sections for landscape layout
    const leftSection = document.createElement('div');
    leftSection.classList.add('left-section');
    
    const rightSection = document.createElement('div');
    rightSection.classList.add('right-section');
    
    // Game logo (left section)
    const logoDiv = document.createElement('div');
    logoDiv.classList.add('game-logo');
    logoDiv.textContent = 'Hoops!';
    logoDiv.style.fontSize = '2.5rem';
    logoDiv.style.fontWeight = 'bold';
    logoDiv.style.textAlign = 'center';
    logoDiv.style.width = '100%';
    logoDiv.style.display = 'flex';
    logoDiv.style.justifyContent = 'center';
    logoDiv.style.alignItems = 'center';
    leftSection.appendChild(logoDiv);
    
    // Avatar selector (left section)
    const avatarSection = document.createElement('div');
    avatarSection.classList.add('form-group');
    
    const avatarLabel = document.createElement('label');
    avatarLabel.textContent = `Choose Your Player`;
    avatarLabel.setAttribute('for', 'avatar-selector');
    avatarLabel.style.marginBottom = '10px';
    avatarLabel.style.textAlign = 'center';
    avatarLabel.style.width = '100%';
    avatarLabel.style.display = 'block';
    avatarSection.appendChild(avatarLabel);
    
    const carousel = document.createElement('div');
    carousel.classList.add('carousel');
    carousel.style.display = 'flex';
    carousel.style.alignItems = 'center';
    carousel.style.justifyContent = 'center';
    carousel.style.position = 'relative';
    carousel.style.width = '100%';
    
    // Carousel navigation - previous button
    const prevButton = document.createElement('button');
    prevButton.textContent = '<';
    prevButton.style.width = '40px';
    prevButton.style.height = '40px';
    prevButton.style.margin = '0 5px';
    prevButton.style.fontSize = '1.8rem';
    prevButton.style.fontWeight = 'bold';
    prevButton.style.display = 'flex';
    prevButton.style.justifyContent = 'center';
    prevButton.style.alignItems = 'center';
    prevButton.style.borderRadius = '4px';
    prevButton.style.cursor = 'pointer';
    prevButton.addEventListener('click', () => this.changeAvatar(-1));
    carousel.appendChild(prevButton);
    
    // Avatar container
    const avatarContainer = document.createElement('div');
    avatarContainer.classList.add('avatar-container');
    avatarContainer.style.display = 'flex';
    avatarContainer.style.justifyContent = 'center';
    avatarContainer.style.minWidth = '180px';
    
    // Add avatar options to carousel
    for (let i = 0; i < this.avatarOptions.length; i++) {
      const avatarOption = document.createElement('div');
      avatarOption.classList.add('avatar-option');
      avatarOption.style.display = i === this.currentAvatarIndex ? 'flex' : 'none';
      avatarOption.style.flexDirection = 'column';
      avatarOption.style.alignItems = 'center';
      
      // Avatar image container
      const avatarPreview = document.createElement('div');
      avatarPreview.classList.add('avatar-preview');
      avatarPreview.style.width = '120px';
      avatarPreview.style.height = '120px';
      avatarPreview.style.backgroundColor = this.avatarOptions[i].color;
      avatarPreview.style.borderRadius = '50%';
      
      // Add preview image
      const previewImage = document.createElement('img');
      previewImage.src = this.avatarOptions[i].previewPath;
      previewImage.style.width = '100%';
      previewImage.style.height = '100%';
      previewImage.style.objectFit = 'cover';
      previewImage.style.borderRadius = '50%';
      
      avatarPreview.appendChild(previewImage);
      avatarOption.appendChild(avatarPreview);

      // Add avatar name
      const avatarName = document.createElement('div');
      avatarName.textContent = this.avatarOptions[i].name.charAt(0).toUpperCase() + this.avatarOptions[i].name.slice(1);
      avatarName.style.textAlign = 'center';
      avatarName.style.marginTop = '8px';
      avatarName.style.fontSize = '1rem';
      avatarName.style.fontWeight = '500';
      avatarOption.appendChild(avatarName);

      avatarContainer.appendChild(avatarOption);
    }
    
    carousel.appendChild(avatarContainer);
    
    // Carousel navigation - next button
    const nextButton = document.createElement('button');
    nextButton.textContent = '>';
    nextButton.style.width = '40px';
    nextButton.style.height = '40px';
    nextButton.style.margin = '0 5px';
    nextButton.style.fontSize = '1.8rem';
    nextButton.style.fontWeight = 'bold';
    nextButton.style.display = 'flex';
    nextButton.style.justifyContent = 'center';
    nextButton.style.alignItems = 'center';
    nextButton.style.borderRadius = '4px';
    nextButton.style.cursor = 'pointer';
    nextButton.addEventListener('click', () => this.changeAvatar(1));
    carousel.appendChild(nextButton);
    
    avatarSection.appendChild(carousel);
    leftSection.appendChild(avatarSection);
    
    // Game mode selector (right section)
    const gameModeSection = document.createElement('div');
    gameModeSection.classList.add('form-group');
    
    const gameModeLabel = document.createElement('label');
    gameModeLabel.textContent = 'Game Mode';
    gameModeSection.appendChild(gameModeLabel);
    
    const modeRadioGroup = document.createElement('div');
    modeRadioGroup.classList.add('radio-group');
    
    // Open game option
    const radioOpen = document.createElement('input');
    radioOpen.type = 'radio';
    radioOpen.id = 'game-mode-open';
    radioOpen.name = 'game-mode';
    radioOpen.checked = this.gameMode === 'open';
    radioOpen.addEventListener('change', () => {
      this.gameMode = 'open';
      gameCodeSection.style.display = 'none';
    });
    
    const labelOpen = document.createElement('label');
    labelOpen.htmlFor = 'game-mode-open';
    labelOpen.textContent = 'Open';
    
    // Private game option
    const radioPrivate = document.createElement('input');
    radioPrivate.type = 'radio';
    radioPrivate.id = 'game-mode-private';
    radioPrivate.name = 'game-mode';
    radioPrivate.checked = this.gameMode === 'private';
    radioPrivate.addEventListener('change', () => {
      this.gameMode = 'private';
      gameCodeSection.style.display = 'block';
    });
    
    const labelPrivate = document.createElement('label');
    labelPrivate.htmlFor = 'game-mode-private';
    labelPrivate.textContent = 'Private';
    
    modeRadioGroup.appendChild(radioOpen);
    modeRadioGroup.appendChild(labelOpen);
    modeRadioGroup.appendChild(radioPrivate);
    modeRadioGroup.appendChild(labelPrivate);
    
    gameModeSection.appendChild(modeRadioGroup);
    rightSection.appendChild(gameModeSection);
    
    // Game code section (right section)
    const gameCodeSection = document.createElement('div');
    gameCodeSection.classList.add('form-group');
    gameCodeSection.style.display = this.gameMode === 'private' ? 'block' : 'none';
    
    const gameCodeLabel = document.createElement('label');
    gameCodeLabel.textContent = 'Enter Game Code (Leave blank to create a new game)';
    gameCodeSection.appendChild(gameCodeLabel);
    
    const gameCodeInput = document.createElement('input');
    gameCodeInput.type = 'text';
    gameCodeInput.value = this.gameCode;
    gameCodeInput.placeholder = 'Enter game code...';
    gameCodeInput.addEventListener('input', (e) => {
      this.gameCode = (e.target as HTMLInputElement).value;
    });
    
    gameCodeSection.appendChild(gameCodeInput);
    rightSection.appendChild(gameCodeSection);
    
    // Team setup section (right section)
    const teamSetupSection = document.createElement('div');
    teamSetupSection.classList.add('form-group');
    
    const teamNameLabel = document.createElement('label');
    teamNameLabel.textContent = 'Name';
    teamSetupSection.appendChild(teamNameLabel);
    
    const teamNameInput = document.createElement('input');
    teamNameInput.type = 'text';
    teamNameInput.value = this.playerName;
    teamNameInput.maxLength = 16;
    teamNameInput.addEventListener('input', (e) => {
      this.playerName = (e.target as HTMLInputElement).value;
    });
    
    teamSetupSection.appendChild(teamNameInput);
    rightSection.appendChild(teamSetupSection);
    
    
    // Play button (right section)
    const playButton = document.createElement('button');
    playButton.textContent = 'Play';
    playButton.style.height = '48px';
    playButton.style.fontSize = '1.2rem';
    playButton.style.fontWeight = 'bold';
    playButton.addEventListener('click', () => this.handlePlayClick());
    rightSection.appendChild(playButton);

    // Error message container (hidden by default)
    const errorMessage = document.createElement('div');
    errorMessage.id = 'error-message';
    errorMessage.style.display = 'none';
    errorMessage.style.color = 'red';
    errorMessage.style.marginTop = '1rem';
    errorMessage.style.textAlign = 'center';
    errorMessage.style.width = '100%';
    rightSection.appendChild(errorMessage);
    
    
    
    // Add sections to content
    content.appendChild(leftSection);
    content.appendChild(rightSection);
    
    // Add the content to the container
    this.container.appendChild(content);
  }
  
  private changeAvatar(direction: number): void {
    const newIndex = (this.currentAvatarIndex + direction + this.avatarOptions.length) % this.avatarOptions.length;
    
    // Hide current avatar
    const currentAvatar = this.container.querySelector('.avatar-container .avatar-option:nth-child(' + (this.currentAvatarIndex + 1) + ')');
    if (currentAvatar) {
      (currentAvatar as HTMLElement).style.display = 'none';
    }
    
    // Show new avatar
    const newAvatar = this.container.querySelector('.avatar-container .avatar-option:nth-child(' + (newIndex + 1) + ')');
    if (newAvatar) {
      (newAvatar as HTMLElement).style.display = 'flex';
    }
    
    // Update the current avatar index
    this.currentAvatarIndex = newIndex;
    
    // Update the display of the selected avatar color name 
    const nameLabel = this.container.querySelector('label[for="avatar-selector"]');
    if (nameLabel) {
      nameLabel.textContent = `Choose Your Player`;
    }
  }
  
  private handlePlayClick(): void {
    // Validate player name
    if (!this.playerName.trim()) {
      this.showError('Please enter a player name.');
      return;
    }

    if (this.playerName.length > 16) {
      this.showError('Player name must be 16 characters or less.');
      return;
    }
    
    // Store the player name and avatar selection in local storage
    localStorage.setItem('playerName', this.playerName.trim());
    localStorage.setItem('selectedAvatarIndex', this.currentAvatarIndex.toString());
    
    // Prepare the game options
    const options: GameOptions = {
      avatarIndex: this.currentAvatarIndex,
      gameMode: this.gameMode,
      gameCode: this.gameCode || undefined,
      playerName: this.playerName.trim()
    };
    
    // Only include isHomeTeam if we're in a private game with a code
    if (this.gameMode === 'private' && this.gameCode) {
      (options as any).isHomeTeam = this.isHomeTeam;
    }
    
    // Call the callback with the game options
    this.startGameCallback(options);
  }
  
  /**
   * Update the loading progress bar
   * @param progress The loading progress percentage (0-100)
   */
  public updateLoadingProgress(progress: number): void {
    if (!this.loadingProgressBar || !this.loadingProgressText) {
      return;
    }
    
    // Clamp progress between 0 and 100
    const clampedProgress = Math.max(0, Math.min(100, progress));
    
    // Update the progress bar width
    this.loadingProgressBar.style.width = `${clampedProgress}%`;
    
    // Update the progress text
    this.loadingProgressText.textContent = `Loading assets: ${Math.round(clampedProgress)}%`;
    
    // Hide progress bar when complete
    if (clampedProgress >= 100) {
      if (this.loadingProgressBar.parentElement) {
        this.loadingProgressBar.parentElement.style.display = 'none';
      }
      this.loadingProgressText.style.display = 'none';
    } else {
      if (this.loadingProgressBar.parentElement) {
        this.loadingProgressBar.parentElement.style.display = 'block';
      }
      this.loadingProgressText.style.display = 'block';
    }
  }
} 